import { DOMService, isHTMLElement } from '@arts/utilities';
import { INPUT_CLASSES, PARENT_SELECTORS, FORM_TAG } from './constants';
export class ArtsForms {
    initialized = false;
    config;
    handlers = {
        focusIn: this.onFocusIn.bind(this),
        focusOut: this.onFocusOut.bind(this),
        reset: this.onReset.bind(this),
        change: this.onChange.bind(this),
        formAdded: this.onFormAdded.bind(this),
        animationStart: this.onAnimationStart.bind(this)
    };
    domService;
    constructor(config) {
        this.domService = DOMService;
        this.config = {
            inputSelector: INPUT_CLASSES.BASE,
            parentSelectors: PARENT_SELECTORS,
            classNames: {
                notEmpty: INPUT_CLASSES.NOT_EMPTY,
                focused: INPUT_CLASSES.FOCUSED,
                checked: INPUT_CLASSES.CHECKED
            },
            ...config
        };
    }
    get isInitialized() {
        return this.initialized;
    }
    init() {
        if (this.initialized) {
            return this;
        }
        this.initFloatingLabels();
        this.attachEvents();
        this.initialized = true;
        return this;
    }
    destroy() {
        if (!this.initialized) {
            return this;
        }
        this.detachEvents();
        this.initialized = false;
        return this;
    }
    update() {
        if (!this.initialized) {
            return this;
        }
        this.initFloatingLabels();
        return this;
    }
    /**
     * Initialize floating labels for all form inputs
     */
    initFloatingLabels() {
        const inputs = this.domService.querySelectorAll(this.domService.getDocumentElement(), `.${this.config.inputSelector}`);
        inputs.forEach((input) => {
            const inputState = this.getInputState(input);
            this.updateInputState(inputState);
        });
    }
    /**
     * Get current state of an input element
     */
    getInputState(element) {
        const controlWrapper = this.domService.closest(element, this.config.parentSelectors);
        const hasValue = Boolean(element.value?.length);
        const hasPlaceholder = Boolean(element.placeholder?.length);
        const isFocused = document.activeElement === element;
        const isChecked = element.type === 'checkbox'
            ? element.checked
            : undefined;
        return {
            element,
            controlWrapper,
            hasValue,
            hasPlaceholder,
            isFocused,
            isChecked
        };
    }
    /**
     * Update input state based on current conditions
     */
    updateInputState(state) {
        const { element, controlWrapper, hasValue, hasPlaceholder, isFocused, isChecked } = state;
        // Handle checkbox state
        if (typeof isChecked === 'boolean') {
            this.setCheckedState(element, controlWrapper, isChecked);
        }
        // Handle focus state
        if (isFocused) {
            this.setFocusState(element, controlWrapper);
            return;
        }
        // Handle value state
        if (hasValue || hasPlaceholder) {
            this.setNotEmptyState(element, controlWrapper);
        }
        else {
            this.setEmptyState(element, controlWrapper);
        }
    }
    /**
     * Set not empty state on input and wrapper
     */
    setNotEmptyState(element, controlWrapper) {
        this.domService.addClass(element, this.config.classNames.notEmpty);
        if (controlWrapper) {
            this.domService.addClass(controlWrapper, this.config.classNames.notEmpty);
        }
    }
    /**
     * Set empty state on input and wrapper
     */
    setEmptyState(element, controlWrapper) {
        this.domService.removeClass(element, this.config.classNames.focused);
        this.domService.removeClass(element, this.config.classNames.notEmpty);
        if (controlWrapper) {
            this.domService.removeClass(controlWrapper, this.config.classNames.focused);
            this.domService.removeClass(controlWrapper, this.config.classNames.notEmpty);
        }
    }
    /**
     * Set focus state on input and wrapper
     */
    setFocusState(element, controlWrapper) {
        this.domService.addClass(element, this.config.classNames.focused);
        this.domService.removeClass(element, this.config.classNames.notEmpty);
        if (controlWrapper) {
            this.domService.addClass(controlWrapper, this.config.classNames.focused);
            this.domService.removeClass(controlWrapper, this.config.classNames.notEmpty);
        }
    }
    /**
     * Set checked state on checkbox input and wrapper
     */
    setCheckedState(element, controlWrapper, checked) {
        this.domService.toggleClass(element, this.config.classNames.checked, checked);
        if (controlWrapper) {
            this.domService.toggleClass(controlWrapper, this.config.classNames.checked, checked);
        }
    }
    /**
     * Remove focus state from input and wrapper
     */
    removeFocusState(element, controlWrapper) {
        this.domService.removeClass(element, this.config.classNames.focused);
        if (controlWrapper) {
            this.domService.removeClass(controlWrapper, this.config.classNames.focused);
        }
    }
    /**
     * Check if target is a form input
     */
    isTargetInput(target) {
        return isHTMLElement(target) && this.domService.matches(target, `.${this.config.inputSelector}`);
    }
    /**
     * Check if target is a form element
     */
    isTargetForm(target) {
        return isHTMLElement(target) && target.tagName === FORM_TAG;
    }
    /**
     * Attach event listeners
     */
    attachEvents() {
        const documentElement = this.domService.getDocumentElement();
        documentElement.addEventListener('focusin', this.handlers.focusIn);
        documentElement.addEventListener('focusout', this.handlers.focusOut);
        documentElement.addEventListener('reset', this.handlers.reset);
        documentElement.addEventListener('change', this.handlers.change);
        documentElement.addEventListener('arts/forms/form_added', this.handlers.formAdded);
        documentElement.addEventListener('animationstart', this.handlers.animationStart);
    }
    /**
     * Detach event listeners
     */
    detachEvents() {
        const documentElement = this.domService.getDocumentElement();
        documentElement.removeEventListener('focusin', this.handlers.focusIn);
        documentElement.removeEventListener('focusout', this.handlers.focusOut);
        documentElement.removeEventListener('reset', this.handlers.reset);
        documentElement.removeEventListener('change', this.handlers.change);
        documentElement.removeEventListener('arts/forms/form_added', this.handlers.formAdded);
        documentElement.removeEventListener('animationstart', this.handlers.animationStart);
    }
    /**
     * Handle focus in events
     */
    onFocusIn(event) {
        if (!this.isTargetInput(event.target)) {
            return;
        }
        const inputState = this.getInputState(event.target);
        this.setFocusState(inputState.element, inputState.controlWrapper);
    }
    /**
     * Handle focus out events
     */
    onFocusOut(event) {
        if (!this.isTargetInput(event.target)) {
            return;
        }
        const inputState = this.getInputState(event.target);
        // Always remove focus state first
        this.removeFocusState(inputState.element, inputState.controlWrapper);
        // Then add not-empty state if needed
        if (inputState.hasValue || inputState.hasPlaceholder) {
            this.setNotEmptyState(inputState.element, inputState.controlWrapper);
        }
    }
    /**
     * Handle form reset events
     */
    onReset(event) {
        if (!this.isTargetForm(event.target)) {
            return;
        }
        const inputs = this.domService.querySelectorAll(event.target, `.${this.config.inputSelector}`);
        // Use setTimeout to ensure DOM updates from form reset have completed
        setTimeout(() => {
            inputs.forEach((input) => {
                const inputState = this.getInputState(input);
                this.updateInputState(inputState);
            });
        }, 0);
    }
    /**
     * Handle change events (mainly for checkboxes)
     */
    onChange(event) {
        if (!this.isTargetInput(event.target)) {
            return;
        }
        const inputState = this.getInputState(event.target);
        if (typeof inputState.isChecked === 'boolean') {
            this.setCheckedState(inputState.element, inputState.controlWrapper, inputState.isChecked);
        }
    }
    /**
     * Handle custom form added events
     */
    onFormAdded(event) {
        const customEvent = event;
        if (!customEvent.detail?.form) {
            return;
        }
        this.updateFormInputsState(customEvent.detail.form);
    }
    /**
     * Handle animation start events (for auto-fill detection)
     */
    onAnimationStart(event) {
        if (!this.isTargetInput(event.target)) {
            return;
        }
        // Check if this is an autofill animation
        const animationEvent = event;
        const normalizedAnimationName = animationEvent.animationName.toLowerCase();
        if (normalizedAnimationName.includes('autofill')) {
            // Wait a small time to ensure the autofill has been applied
            setTimeout(() => {
                const inputState = this.getInputState(event.target);
                this.updateInputState(inputState);
            }, 0);
        }
    }
    /**
     * Update floating label states for all inputs within a specific form
     */
    updateFormInputsState(form) {
        const inputs = this.domService.querySelectorAll(form, `.${this.config.inputSelector}`);
        inputs.forEach((input) => {
            const inputState = this.getInputState(input);
            this.updateInputState(inputState);
        });
    }
}
